import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';
import { paymentStoreName } from '../utils/iap';
import { isIOS } from '../utils/capacitor';

// In the apps, purchases go through the App Store / Google Play, and the copy
// has to say so — describing Stripe billing there reads to App Review as
// selling subscriptions outside In-App Purchase (Guideline 3.1.1).
const APP_STORE = paymentStoreName();

const categories = [
  {
    id: 'getting-started',
    icon: '🚀',
    label: 'Getting Started',
    questions: [
      {
        q: 'What is Dreamy Bedtime?',
        a: 'Dreamy Bedtime is a personalised bedtime story platform for families. You create a child profile with your child\'s name, age, and interests — and we generate a unique AI-written story starring them, complete with a custom watercolour illustration, in about 15 seconds. Stories can be narrated aloud, exported to PDF, and saved to your library forever.',
      },
      {
        q: 'How do I get started?',
        a: 'Create a free account, add your child\'s profile (first name, age, interests), then hit Generate Story. Your first story is ready in about 15 seconds. No credit card required for the Free plan.',
      },
      {
        q: 'Do I need to install anything?',
        a: 'No installation required — Dreamy Bedtime runs entirely in your browser. You can also install it as a Progressive Web App (PWA) for quick access. On iPhone: open in Safari → tap the Share button → Add to Home Screen. On Android: open in Chrome → tap the three-dot menu → Add to Home Screen. On desktop: click the install icon in your browser\'s address bar.',
      },
      {
        q: 'Can I use Dreamy Bedtime on any device?',
        a: 'Yes. Dreamy Bedtime works on any device with a modern web browser — phone, tablet, or desktop. The app is fully responsive and optimised for mobile reading at bedtime.',
      },
      {
        q: 'How long does it take to generate a story?',
        a: 'Stories are generated in approximately 15 seconds. The illustration appears shortly after the story text, usually within 30–60 seconds. If you generate a story and close the browser, the story will be ready in your library when you return.',
      },
    ],
  },
  {
    id: 'plans-billing',
    icon: '💳',
    label: 'Plans & Billing',
    questions: [
      {
        q: 'What plans are available?',
        a: 'We offer three plans:\n\n• Free — 5 stories/month, 1 child profile, story library, reading reports. No credit card required.\n\n• Basic ($14/month or $154/year) — 60 stories, 30 AI narrations, 3 photo illustrations, 20 illustration regenerations/month, up to 3 child profiles, Story Series, Story Quiz, PDF export.\n\n• Family ($22/month or $242/year) — 120 stories, 40 AI narrations, 5 voice-clone narrations, 6 photo illustrations, 40 illustration regenerations/month, unlimited profiles, voice cloning, grandparent sharing, priority support.',
      },
      {
        q: 'Is there a free plan?',
        a: 'Yes — the Free plan is permanently free with no credit card required. You get 5 stories per month, no time limit, no commitment. Upgrade only when you want more.',
      },
      {
        q: 'Can I switch between plans?',
        a: APP_STORE
          ? `Yes. Choose the new plan on the Pricing page and ${APP_STORE} handles the change. Upgrades take effect immediately; downgrades take effect at the end of your current billing period.`
          : 'Yes, you can upgrade or downgrade at any time from your account Settings. Upgrades take effect immediately and Stripe prorates the difference. Downgrades take effect at the end of your current billing period.',
      },
      {
        q: 'Do unused stories carry over to the next month?',
        a: 'Monthly allowances (stories, narrations, illustration regenerations, photo illustrations) reset on the 1st of each calendar month and do not carry over. However, top-up pack credits you purchase separately do carry over indefinitely.',
      },
      {
        q: 'What payment methods do you accept?',
        a: APP_STORE
          ? `Purchases in the app are made through ${APP_STORE}, charged to your ${isIOS() ? 'Apple ID' : 'Google account'}. We never see or store your payment details.`
          : 'We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) via Stripe. We do not store your card details — all payment processing is handled securely by Stripe.',
      },
      {
        q: 'Is annual billing cheaper?',
        a: 'Yes. Annual billing saves you approximately 8% compared to monthly. Basic annually is $154 (equivalent to ~$12.83/month). Family annually is $242 (equivalent to ~$20.17/month). Annual plans are billed in one upfront payment.',
      },
      {
        q: 'What currencies do you support?',
        a: 'Pricing is automatically displayed in your local currency (USD, GBP, EUR, CAD, AUD, INR, and others) based on your location. The price at checkout is final.',
      },
      {
        q: 'What are top-up packs?',
        a: 'If you run out of your monthly allowance, you can purchase one-time top-up packs to extend your usage without upgrading your plan:\n\n• Story Pack (+10 stories) — $3.99\n• Narration Pack (+10 narrations) — $2.99\n• Voice Pack (+5 clone narrations) — $5.99\n• Illustration Pack (+10 regenerations) — $2.99\n• Photo Illustration Pack (+3 photo illustrations) — $4.99\n\nTop-up credits never expire and carry over each month.',
      },
    ],
  },
  {
    id: 'cancellation-refunds',
    icon: '↩️',
    label: 'Cancellation & Refunds',
    questions: [
      {
        q: 'How do I cancel my subscription?',
        a: 'Go to Settings → Subscription → Cancel Plan. Cancellation takes effect at the end of your current billing period — you keep access to all paid features until then. You will not be charged again after cancellation.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'We do not offer refunds for partial months or unused allowances on monthly plans. For annual plans, you may request a refund within 14 days of the initial purchase if you have not generated more than 5 stories. If you believe you were charged in error, contact us within 14 days at support@dreamybedtime.com and we will investigate promptly.',
      },
      {
        q: 'What happens to my stories if I cancel?',
        a: 'Your story library remains accessible on the Free plan after cancellation — you keep your stories forever. Some features (narration, voice cloning, advanced exports) will require a paid plan to use again, but your existing content is not deleted.',
      },
      {
        q: 'Are top-up credits refundable?',
        a: 'Top-up credits are non-refundable once purchased, as they grant immediate access to service capacity. Unused credits carry over indefinitely, so they are never wasted.',
      },
    ],
  },
  {
    id: 'stories-features',
    icon: '📖',
    label: 'Stories & Features',
    questions: [
      {
        q: 'What languages are available?',
        a: 'Stories can be generated in 11 languages: English, Spanish, French, Hindi, German, Portuguese, Arabic, Chinese (Simplified), Japanese, Italian, and Gujarati. You can set a default language per child profile or change it each time you generate.',
      },
      {
        q: 'How many child profiles can I create?',
        a: 'Free plan: 1 child profile. Basic plan: up to 3 profiles. Family plan: unlimited profiles. Each child gets their own personalised settings, story preferences, interests, and reading history.',
      },
      {
        q: 'Can I have multiple children as the heroes of one story?',
        a: 'Yes — Family Stories (Basic and Family plans) let you select multiple child profiles and generate one story featuring all of them as the heroes together. Great for siblings.',
      },
      {
        q: 'What is the Story Approval Queue?',
        a: 'The Approval Queue is an optional parental control feature. When enabled, every newly generated story is held for your review before it appears in your child\'s reading list. You can approve or reject each story. Useful if you share a device with your child and want to preview content first. Enable it in Settings → Approval Queue.',
      },
      {
        q: 'What is Story Series?',
        a: 'Story Series (Basic and Family plans) lets you create multi-chapter adventure stories. You set up the series — title, characters, setting, tone — and generate chapters one at a time. Each chapter picks up exactly where the last one ended, maintaining consistency across the whole arc.',
      },
      {
        q: 'What is the Story Quiz?',
        a: 'After reading a story, you can generate a short comprehension quiz (Basic and Family plans) with 3 age-appropriate questions about the story. A fun way to encourage reading engagement and check understanding.',
      },
      {
        q: 'Can I export or download stories?',
        a: 'Yes. Basic and Family plan subscribers can export any story as a PDF — great for printing or sharing offline. Story illustrations can be downloaded as image files from your story library.',
      },
      {
        q: 'Can I regenerate an illustration I don\'t like?',
        a: 'Yes — Basic plan gets 20 illustration regenerations per month, Family plan gets 40. Open the story, tap the illustration, and select Regenerate. A new unique illustration will be created in seconds.',
      },
      {
        q: 'What are real-world learning topics?',
        a: 'Basic and Family plan subscribers can add an educational topic to any story — things like "solar system", "how plants grow", "fractions", or "the water cycle". The AI weaves the learning content naturally into the narrative so it\'s educational but still feels like a proper bedtime story.',
      },
    ],
  },
  {
    id: 'narration-voice',
    icon: '🎙️',
    label: 'Narration & Voice',
    questions: [
      {
        q: 'What is AI narration?',
        a: 'AI narration (Basic and Family plans) reads your story aloud using a warm, expressive text-to-speech voice. Just open a story and tap the Listen button. Basic: 30 narrations/month. Family: 40 narrations/month.',
      },
      {
        q: 'What is voice cloning?',
        a: 'Voice cloning (Family plan only) lets you record 30–60 seconds of your own voice, and we create a personalised voice model. Every story can then be narrated in your voice — so children hear Mum or Dad reading the story even when you\'re not there. You get 5 voice-clone narrations per month, plus extra via the Voice Pack top-up.',
      },
      {
        q: 'Is my voice recording safe?',
        a: 'Yes. Your voice recording is transmitted over HTTPS and sent to Cartesia (our voice technology provider) to create a voice model. Cartesia is contractually prohibited from using your voice for any other purpose. You can delete your voice profile at any time from Settings → Voice Clone.',
      },
      {
        q: 'Can narrations be in languages other than English?',
        a: 'Yes. AI narration supports all 11 languages Dreamy Bedtime offers. The voice will automatically narrate in the language the story was generated in. Voice cloning also supports multilingual narration.',
      },
    ],
  },
  {
    id: 'illustrations',
    icon: '🎨',
    label: 'Illustrations & Photos',
    questions: [
      {
        q: 'What kind of illustrations does Dreamy Bedtime generate?',
        a: 'Each story gets a unique, full-colour watercolour-style illustration depicting the most vivid scene from the story. Illustrations are generated by our AI image model and are optimised to be child-appropriate, warm, and storybook-quality.',
      },
      {
        q: 'What is the photo illustration feature?',
        a: 'Photo illustrations (Basic and Family plans) let you upload a photo of your child, and our AI generates a face-accurate, illustrated version of them as the hero of the story\'s illustration. Basic: 3 photo illustrations/month. Family: 6/month. The result is a personalised storybook that literally looks like your child is in it.',
      },
      {
        q: 'How is my child\'s photo stored and used?',
        a: 'Your child\'s photo is stored securely on our servers and used only to generate illustrations within your account. It is not used for AI training, advertising, or any other purpose. You can delete the stored photo at any time from your child\'s profile settings, and it will be removed within 30 days.',
      },
    ],
  },
  {
    id: 'sharing-community',
    icon: '🌍',
    label: 'Sharing & Community',
    questions: [
      {
        q: 'How does grandparent sharing work?',
        a: 'Family plan subscribers can generate a private share link for any story. Send the link to grandparents, relatives, or anyone you choose. They can read the story and leave emoji reactions without needing an account. Links are private — not indexed or publicly searchable.',
      },
      {
        q: 'What is the Community page?',
        a: 'The Community page is a curated collection of stories that Dreamy Bedtime subscribers have chosen to share publicly. You can browse stories by theme, age group, or language. It\'s a source of inspiration and a place to discover how other families use the platform. Sharing to the community is always optional.',
      },
      {
        q: 'Can I un-publish a community story?',
        a: 'Yes. Open the story in your library and select Remove from Community. The story will no longer appear on the public community page.',
      },
      {
        q: 'How does the referral programme work?',
        a: 'Paid subscribers can create referral links to share with friends. When a friend claims your link and receives their free gift story, you earn a bonus story credit as a thank-you. Referral links have a limited number of claims per month and an expiry date.',
      },
    ],
  },
  {
    id: 'gift',
    icon: '🎁',
    label: 'Gift Stories & Gift Subscriptions',
    questions: [
      {
        q: 'What is a gift story?',
        a: 'A gift story is a one-time purchase where you provide a child\'s name, age, and interests, and we generate a personalised story that is emailed to the recipient. Perfect for birthdays, new siblings, or any occasion. The recipient receives a beautifully formatted email with the full story and illustration.',
      },
      {
        q: 'What is a gift subscription?',
        a: 'A gift subscription is a prepaid Dreamy Bedtime plan (Basic or Family) that you purchase for someone else. The recipient receives an email with a claim link. They have 90 days to redeem it and activate their subscription. Available in 1-month, 3-month, 6-month, and 12-month options.',
      },
      {
        q: 'Are gifts refundable?',
        a: 'Gift stories are non-refundable once story generation has been initiated. Gift subscriptions are non-refundable, including if the recipient does not redeem within the 90-day claim window. Please double-check the recipient\'s email address before purchasing. If a technical error on our part prevented delivery, contact us within 14 days.',
      },
      {
        q: 'What if the recipient\'s email address was wrong?',
        a: 'We are unable to re-route gifts to a different email address after purchase. Please check the address carefully before completing checkout. If you believe the email was not delivered due to a technical error on our part (not a wrong address), contact us within 14 days.',
      },
    ],
  },
  {
    id: 'privacy-safety',
    icon: '🔒',
    label: 'Privacy & Safety',
    questions: [
      {
        q: 'Is Dreamy Bedtime safe for children?',
        a: 'Yes. All story content goes through automated content moderation before being saved. Stories are generated with age-appropriate guidelines baked into every prompt. An optional Approval Queue lets parents review stories before children see them. We never show advertising to any users.',
      },
      {
        q: 'Do you use my data to train AI?',
        a: 'No. We do not use your stories, your child\'s profile, your photos, or your voice recordings to train AI models. We use OpenAI\'s API on a plan that explicitly prohibits use of API inputs for training.',
      },
      {
        q: 'Is Dreamy Bedtime COPPA compliant?',
        a: 'Yes. Dreamy Bedtime is designed for parents and caregivers — not for direct use by children. We do not knowingly collect personal information from children under 13. All data is provided by the adult account holder. Child profiles contain only a first name and age range.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Settings → Account → Delete Account. Your account and all associated data will be deleted within 30 days. Billing records are retained for up to 7 years as required by tax law. Deleting your account cancels any active subscription immediately.',
      },
      {
        q: 'Who can see my child\'s stories?',
        a: 'Your stories are private by default and visible only to you. Stories are only shared publicly if you explicitly choose to publish them to the Community. Grandparent share links are private and not indexed or searchable.',
      },
    ],
  },
  {
    id: 'technical',
    icon: '⚙️',
    label: 'Technical & Account',
    questions: [
      {
        q: 'What if my story generation fails?',
        a: 'If story generation fails, your credit is automatically refunded so you are never charged for a failed attempt. You\'ll see a "Generation failed" status in your library — simply try generating again. If failures persist, contact us at support@dreamybedtime.com.',
      },
      {
        q: 'Can I use Dreamy Bedtime offline?',
        a: 'Story generation requires an internet connection since it uses AI services. However, previously generated stories load in your library and can be read offline if you have installed the PWA and have recently viewed them. Narration also requires a connection.',
      },
      {
        q: 'How do I set up bedtime reminders?',
        a: 'Go to Settings → Bedtime Reminders. You can set one reminder per day per child, with a custom time and message. Your browser will ask for notification permission the first time. Reminders appear as browser notifications — make sure notifications are allowed for dreamybedtime.com in your browser settings.',
      },
      {
        q: 'How do I change my email address or password?',
        a: 'Go to Settings → Account. You can update your name, email address, and password there. Email changes require you to verify the new address.',
      },
      {
        q: 'I\'m not receiving emails from Dreamy Bedtime. What should I do?',
        a: 'Check your spam or junk folder for emails from @dreamybedtime.com. Add support@dreamybedtime.com to your contacts to prevent future emails going to spam. If you still can\'t find them, contact us and we\'ll investigate.',
      },
      {
        q: 'How do I contact support?',
        a: 'Use our Contact page or email support@dreamybedtime.com directly. We typically respond within 24–48 hours on weekdays. Billing issues are treated as priority and we aim to respond same day.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-bottom"
      style={{ borderColor: 'rgba(111,66,193,0.1) !important' }}
    >
      <button
        className="w-100 d-flex align-items-start justify-content-between gap-3 py-3 px-0 bg-transparent border-0 text-start"
        onClick={() => setOpen(o => !o)}
        style={{ cursor: 'pointer' }}
      >
        <span className="fw-semibold" style={{ color: 'var(--text-main)', fontSize: '15px', lineHeight: 1.5 }}>
          {q}
        </span>
        <i
          className={`bi bi-chevron-${open ? 'up' : 'down'} flex-shrink-0 mt-1`}
          style={{ color: '#6f42c1', fontSize: '14px' }}
        />
      </button>
      {open && (
        <div className="pb-3" style={{ fontSize: '15px', color: 'var(--text-muted2)', lineHeight: 1.8, whiteSpace: 'pre-line' }}>
          {a}
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [search, setSearch] = useState('');

  const filtered = search.trim().length > 1
    ? categories.flatMap(c => c.questions.filter(
        item =>
          item.q.toLowerCase().includes(search.toLowerCase()) ||
          item.a.toLowerCase().includes(search.toLowerCase())
      ).map(item => ({ ...item, _category: c.label }))
    )
    : null;

  const activeQuestions = categories.find(c => c.id === activeCategory)?.questions || [];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="FAQ — Frequently Asked Questions"
        description="Answers to common questions about Dreamy Bedtime — plans, billing, features, privacy, cancellation, voice cloning, and more."
        canonical="/faq"
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-dream text-white" style={{ padding: '56px 0 40px' }}>
        <div className="container text-center" style={{ maxWidth: '640px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">❓ FAQ</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,44px)' }}>
            Frequently Asked Questions
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '16px', marginBottom: '28px' }}>
            Everything you need to know about Dreamy Bedtime.
          </p>
          {/* Search */}
          <div className="position-relative" style={{ maxWidth: '420px', margin: '0 auto' }}>
            <i className="bi bi-search position-absolute" style={{ left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '16px' }} />
            <input
              type="text"
              className="form-control rounded-pill ps-5"
              placeholder="Search questions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', fontSize: '15px', padding: '12px 20px 12px 44px' }}
            />
          </div>
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '960px' }}>
        {/* Search results */}
        {filtered !== null ? (
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
            <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "<strong>{search}</strong>"
            </p>
            {filtered.length === 0 ? (
              <div className="text-center py-4">
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
                <p className="text-muted mb-3">No results found. Try different keywords.</p>
                <a href="mailto:support@dreamybedtime.com" className="btn btn-dream rounded-pill px-4">
                  Ask us directly
                </a>
              </div>
            ) : (
              filtered.map((item, i) => (
                <div key={i}>
                  <p className="text-muted mb-1" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6f42c1' }}>{item._category}</p>
                  <FAQItem q={item.q} a={item.a} />
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="row g-4">
            {/* Category sidebar */}
            <div className="col-lg-3">
              <div className="card border-0 shadow-sm p-3 sticky-top" style={{ borderRadius: '16px', top: '80px' }}>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`w-100 text-start btn border-0 rounded-3 py-2 px-3 mb-1 d-flex align-items-center gap-2`}
                    style={{
                      background: activeCategory === cat.id ? 'rgba(111,66,193,0.1)' : 'transparent',
                      color: activeCategory === cat.id ? '#6f42c1' : '#555',
                      fontWeight: activeCategory === cat.id ? 700 : 500,
                      fontSize: '14px',
                    }}
                  >
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Questions */}
            <div className="col-lg-9">
              <div className="card border-0 shadow-sm p-4 p-lg-5" style={{ borderRadius: '20px' }}>
                {(() => {
                  const cat = categories.find(c => c.id === activeCategory);
                  return (
                    <>
                      <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '22px' }}>
                        {cat.icon} {cat.label}
                      </h2>
                      {cat.questions.map((item, i) => (
                        <FAQItem key={i} q={item.q} a={item.a} />
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Still need help */}
        <div className="text-center mt-5 p-4 rounded-4" style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8f0ff)' }}>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            Still have a question?
          </h4>
          <p className="text-muted mb-3" style={{ fontSize: '15px' }}>
            We typically respond within 24–48 hours on weekdays.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link to="/contact" className="btn btn-dream rounded-pill px-4 fw-semibold">
              <i className="bi bi-envelope me-2" />Contact Support
            </Link>
            <a href="mailto:support@dreamybedtime.com" className="btn btn-outline-secondary rounded-pill px-4 fw-semibold" style={{ borderColor: '#6f42c1', color: '#6f42c1' }}>
              support@dreamybedtime.com
            </a>
          </div>
        </div>

        <div className="text-center mt-4">
          <p className="text-muted small">
            <Link to="/guide" style={{ color: '#6f42c1' }}>Help Guide</Link>
            {' '}·{' '}
            <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy Policy</Link>
            {' '}·{' '}
            <Link to="/terms" style={{ color: '#6f42c1' }}>Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
